/**
 * The single NEXT BEST ACTION.
 *
 * ── Why there is exactly one ─────────────────────────────────────────────────
 *
 * `actions.ts` holds twenty-seven rules and returns the FIRST that matches, by
 * priority. That is a deliberate design: a list of eight things to do is a list
 * nobody works, and the top of it is the only part anyone reads anyway. So the
 * server picks, and this renders the pick — it never re-ranks, never filters,
 * and never shows the runners-up.
 *
 * It sits above every panel and is never folded. On a phone, in a yard, with a
 * seller waiting, "what do I do next" is the whole question.
 */

import { useCallback } from 'react'
import Icon from '../Icon'
import { useLocale, type Locale } from '../../lib/i18n'
import { due, isOverdue, TONES } from '../../lib/format'
import { humanise } from '../../lib/domain'
import { toneStyle } from './ui'
import type { ActionView } from './types'

const EN = {
  heading: 'Next best action',
  none: 'Nothing is waiting on you here.',
  noneWhy: 'When something changes — a price, a reply, a missing answer — this is where it appears.',
  unavailable: 'The next action could not be worked out just now.',
} as const

type CopyKey = keyof typeof EN

const ES: Record<CopyKey, string> = {
  heading: 'Lo próximo que hacer',
  none: 'No hay nada esperando por ti aquí.',
  noneWhy: 'Cuando algo cambie — un precio, una contestación, una respuesta que falta — aparece aquí.',
  unavailable: 'No se pudo determinar la próxima acción ahora mismo.',
}

const COPY: Record<Locale, Record<CopyKey, string>> = { en: EN, 'es-PR': ES }

export default function NextAction({ action, failed = false }: { action: ActionView | null; failed?: boolean }) {
  const locale = useLocale()
  const t = useCallback((key: CopyKey) => COPY[locale][key] ?? EN[key], [locale])

  const late = isOverdue(action?.dueOn ?? null)
  const tone = failed ? TONES.quiet : late ? TONES.danger : TONES.brand

  return (
    <section
      style={{
        ...toneStyle(tone),
        borderRadius: 'var(--radius)',
        padding: '12px 14px',
        marginBottom: 12,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 11,
          fontWeight: 800,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          opacity: 0.9,
        }}
      >
        <Icon name="check" size={14} />
        {t('heading')}
      </div>

      {failed ? (
        <p style={{ margin: '6px 0 0', fontSize: 15, lineHeight: 1.4 }}>{t('unavailable')}</p>
      ) : action ? (
        <>
          <p style={{ margin: '6px 0 0', fontSize: 17, fontWeight: 700, lineHeight: 1.35 }}>{action.sentence}</p>
          {action.dueOn && (
            <p style={{ margin: '6px 0 0', fontSize: 13, fontWeight: 700, opacity: 0.9 }}>
              {due(action.dueOn, locale)}
            </p>
          )}
          {/* The rule that fired, small. It is what makes "why am I being told
              this" answerable without opening the code. */}
          <p style={{ margin: '4px 0 0', fontSize: 11, opacity: 0.7 }}>{humanise(action.code)}</p>
        </>
      ) : (
        <>
          <p style={{ margin: '6px 0 0', fontSize: 16, fontWeight: 600 }}>{t('none')}</p>
          <p style={{ margin: '4px 0 0', fontSize: 13, opacity: 0.8 }}>{t('noneWhy')}</p>
        </>
      )}
    </section>
  )
}
